import classnames from 'classnames/bind'
import ArticleCard from 'components/shared/ArticleCard'
import ArticlesGrid from 'components/shared/ArticlesGrid'
import Layout from 'components/shared/Layout'
import LoadMore from 'components/shared/LoadMore'
import NewsletterBanner from 'components/slices/NewsletterBanner'
import SmallArrow from 'components/icons/SmallArrow'
import { Text } from 'components/typo'
import delve from 'dlv'
import { useTranslate } from 'react-polyglot'
import React, { useReducer, useRef } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'

import algolia from 'services/algolia'
import { useRouter } from 'next/router'
import useScrollToPage from 'src/hooks/useScrollToPage'
import css from './styles.module.scss'

const cx = classnames.bind(css)

const NoResults = () => (
  <div className={css.noResults}>
    <Text>No results</Text>
  </div>
)

const search = async (params) => {
  const [index, facetFilters, page] = params
  const algoliaIndex = algolia(index)

  const res = await algoliaIndex.search('', {
    facetFilters,
    page: page || 0,
    hitsPerPage: 6,
  })

  return {
    hits: delve(res, 'hits', []),
    page: delve(res, 'page', 0),
    nbPages: delve(res, 'nbPages', 0),
    nbHits: delve(res, 'nbHits', 0),
  }
}

function reducer(state, action) {
  switch (action.type) {
    case 'fetching':
      return { ...state, isFetching: true }
    case 'nextPage':
      return {
        ...state,
        page: state.page + 1,
      }
    case 'nextPageFetched':
      return {
        ...state,
        isFetching: false,
        hits: [...state.hits, ...action.payload.hits],
      }
    default:
      throw new Error()
  }
}

const ArticlesResults = ({
  className,
  children,
  index,
  adapter,
  pathname,
  newsletter,
  initialData = {},
  withFeatured = false,
  isCustomLoadButton = false,
  isLoadMore = false,
  isLoading = false,
  handleClickLoadMore,
}) => {
  const t = useTranslate()
  const router = useRouter()
  const [state, dispatch] = useReducer(reducer, {
    searchString: '',
    facetFilters: delve(initialData, 'defaultFacets', ''),
    hits: delve(initialData, 'hits', []),
    page:
      parseInt(router.query.page) ||
      delve(initialData, 'page', router.query.page),
    nbPages: delve(initialData, 'nbPages', 0),
    nbHits: delve(initialData, 'nbHits', 0),
    isFetching: false,
  })
  const initialRender = useRef(true)
  const items = adapter ? adapter(state.hits) : state.hits

  useIsomorphicLayoutEffect(() => {
    if (!initialRender.current) {
      dispatch({ type: 'fetching' })
      algoliaSearch('nextPageFetched')
    }
  }, [state.page])

  useIsomorphicLayoutEffect(() => {
    initialRender.current = false
  }, [])

  const [articleBottomRef] = useScrollToPage()

  const algoliaSearch = async (type) => {
    const { hits, nbPages, page, nbHits } = await search([
      index,
      state.facetFilters,
      state.page,
    ])

    dispatch({
      type,
      payload: { hits, nbPages, page, nbHits },
    })
  }

  const canFetchMore = state.page < state.nbPages - 1
  const [first, second, third, fourth, ...results] = items

  const aboveNewsletterBlockArticles = withFeatured
    ? [second, third]
    : [first, second, third, fourth]
  const belowNewsletterBlockArticles = withFeatured
    ? [fourth, ...results]
    : results

  return (
    <div className={cx(css.ArticlesResults, className)}>
      <Layout type="maxWidth">
        <Layout type="wrapper">
          {children}
          {items.length > 0 ? (
            <>
              {first && withFeatured && (
                <Layout
                  className={cx(css.innerWrapper, css.featured)}
                  type="innerWrapper"
                >
                  <ArticleCard featured {...first} />
                </Layout>
              )}
              <>
                {aboveNewsletterBlockArticles?.length > 0 && (
                  <ArticlesGrid
                    className={css.secondRow}
                    duo
                    cards={aboveNewsletterBlockArticles.filter((item) => item)} // Filter for removing undefined articles
                  />
                )}
                {!withFeatured && (
                  <NewsletterBanner
                    newsletterClassName={css.newsletter}
                    newsletter={newsletter}
                  />
                )}
                {belowNewsletterBlockArticles?.length > 0 && (
                  <ArticlesGrid cards={belowNewsletterBlockArticles} />
                )}
                <div ref={articleBottomRef} />
              </>
            </>
          ) : (
            <NoResults />
          )}
          {!isCustomLoadButton ? (
            <>
              {canFetchMore && (
                <LoadMore
                  withPagination
                  loading={state.isFetching}
                  page={state.page}
                  url={pathname}
                  theme="white"
                  className={css.loadMore}
                  children={t('cta.load_more_articles', {
                    _: 'Load More Articles',
                  })}
                  onClick={(event) => {
                    event.preventDefault()
                    dispatch({ type: 'nextPage' })
                  }}
                />
              )}
            </>
          ) : (
            <>
              {isLoadMore && (
                <div
                  className={cx(css.buttonContainer)}
                  onClick={handleClickLoadMore}
                >
                  <button className={cx(css.button, isLoading && css.loading)}>
                    Load More Articles
                    <SmallArrow
                      theme="current-color"
                      className={css.smallArrow}
                    />
                  </button>
                </div>
              )}
            </>
          )}
        </Layout>
      </Layout>
    </div>
  )
}

ArticlesResults.defaultProps = {}

export default ArticlesResults
